import { USER_TYPES } from '@constants/userTypes.ts';
import { ROUTES } from '@router/constants.ts';
import type { UserInfoResponse } from '@routes/___shared___/user.api.ts';
import { getTaskRoute } from '@routes/sidebar/___shared___/utils.ts';
import type {
    TaskAssessorOption,
    UserPathwayTaskAttempt,
} from '@routes/sidebar/learning/___pages___/learning-overview/api/task.api.ts';
import { ASSESSOR_DECISION } from '@routes/sidebar/learning/___pages___/learning-overview/api/task.api.ts';
import type { AttemptLogStatus } from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/constants.ts';
import {
    ATTEMPT_LOG_STATUS,
    STATUS_ICON_MAPPER,
} from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/constants.ts';
import { openInNewTab } from '@utils/navigation.ts';
import dayjs from 'dayjs';

export const isCrewLearnerSignedOff = (userInfo: UserInfoResponse | null) =>
    userInfo?.userType === USER_TYPES.Crew && !userInfo.employmentDetails?.crewEmploymentDetails?.isOnboard;

export const mapTaskAssessorOptions = (assessors: TaskAssessorOption[], excludeAssessorId?: number | null) =>
    assessors
        .filter((assessor) => assessor.id !== excludeAssessorId)
        .map((assessor) => ({
            label: `${assessor.fullName}, ${assessor.rankTitle}`,
            value: assessor.id,
        }));

export const getLearnerAttemptLogStatus = (attempt: UserPathwayTaskAttempt): AttemptLogStatus => {
    if (attempt.isRecalled) {
        return ATTEMPT_LOG_STATUS.RECALLED;
    }

    return attempt.isExemption ? ATTEMPT_LOG_STATUS.PENDING_EXEMPTION : ATTEMPT_LOG_STATUS.PENDING;
};

export const getAssessorAttemptLogStatus = (attempt: UserPathwayTaskAttempt): AttemptLogStatus | null => {
    if (!attempt.evaluatedAtUTC || attempt.isRecalled) {
        return null;
    }

    switch (attempt.decision) {
        case ASSESSOR_DECISION.Closed:
            return ATTEMPT_LOG_STATUS.CLOSED;

        case ASSESSOR_DECISION.RedoRequired:
            return ATTEMPT_LOG_STATUS.REDO_REQUIRED;

        case ASSESSOR_DECISION.ExemptionGranted:
            return ATTEMPT_LOG_STATUS.EXEMPTED;

        case ASSESSOR_DECISION.ExemptionRejected:
            return ATTEMPT_LOG_STATUS.EXEMPTION_REJECTED;

        default:
            return null;
    }
};

export const getAttemptStatus = (attempt: UserPathwayTaskAttempt): AttemptLogStatus =>
    getAssessorAttemptLogStatus(attempt) ?? getLearnerAttemptLogStatus(attempt);

export type EvaluationLogEntry = {
    attempt: UserPathwayTaskAttempt;
    status: AttemptLogStatus;
    isAssessorEvaluation: boolean;
    displayDateUTC: string;
    key: string;
};

/** Splits each attempt into learner and assessor rows when both exist. */
export const buildEvaluationLogEntries = (
    attempts: UserPathwayTaskAttempt[],
    sortOrder: 'asc' | 'desc' = 'desc',
): EvaluationLogEntry[] => {
    const entries: EvaluationLogEntry[] = [];

    for (const attempt of attempts) {
        const learnerStatus = getLearnerAttemptLogStatus(attempt);

        entries.push({
            attempt,
            status: learnerStatus,
            isAssessorEvaluation: false,
            displayDateUTC: attempt.submittedAtUTC,
            key: `${attempt.number}-${attempt.submittedAtUTC}-learner`,
        });

        const assessorStatus = getAssessorAttemptLogStatus(attempt);
        if (assessorStatus !== null && attempt.evaluatedAtUTC) {
            entries.push({
                attempt,
                status: assessorStatus,
                isAssessorEvaluation: true,
                displayDateUTC: attempt.evaluatedAtUTC,
                key: `${attempt.number}-${attempt.evaluatedAtUTC}-assessor`,
            });
        }
    }

    return entries.sort((left, right) => {
        const displayDateDifference = dayjs(left.displayDateUTC).valueOf() - dayjs(right.displayDateUTC).valueOf();
        return sortOrder === 'asc' ? displayDateDifference : -displayDateDifference;
    });
};

export type EvaluationLogStatusLabels = {
    redo: string;
    closed: string;
    exempted: string;
    exemptionRejected: string;
    submitted: string;
};

export const getAttemptStatusLabel = (attempt: UserPathwayTaskAttempt, labels: EvaluationLogStatusLabels): string => {
    switch (attempt.decision) {
        case ASSESSOR_DECISION.Closed:
            return labels.closed;

        case ASSESSOR_DECISION.RedoRequired:
            return labels.redo;

        case ASSESSOR_DECISION.ExemptionGranted:
            return labels.exempted;

        case ASSESSOR_DECISION.ExemptionRejected:
            return labels.exemptionRejected;

        default:
            return '';
    }
};

export const getEvaluationLogEntryViewData = (
    { attempt, status, isAssessorEvaluation }: EvaluationLogEntry,
    statusLabels: EvaluationLogStatusLabels,
) => {
    const statusLabel =
        status === ATTEMPT_LOG_STATUS.PENDING_EXEMPTION
            ? statusLabels.submitted
            : getAttemptStatusLabel(attempt, statusLabels);

    const isFirstAssesseeSubmission = !isAssessorEvaluation && attempt.number === 1;

    return {
        showAssessorPerson: isAssessorEvaluation,
        showStatusPill: isAssessorEvaluation || status === ATTEMPT_LOG_STATUS.PENDING_EXEMPTION,
        showAssessorComments: Boolean(attempt.assessorComments) && !isFirstAssesseeSubmission,
        statusLabel,
        StatusIcon: STATUS_ICON_MAPPER[status],
        isInactiveRatings: attempt.isExemption,
        evidenceFileId: attempt.evidenceFileId,
    };
};

export const getAssessorText = (
    assessorId: number | null,
    assessors: TaskAssessorOption[],
    assessorName?: string | null,
    assessorRank?: string | null,
) => {
    const fromAttempt = [assessorName, assessorRank].filter(Boolean).join(', ');
    if (fromAttempt) {
        return fromAttempt;
    }

    const assessor = assessors.find(({ id }) => id === assessorId);

    return assessor ? [assessor.fullName, assessor.rankTitle].filter(Boolean).join(', ') : '';
};

export const getLogPersonText = (
    showStatusText: boolean,
    assesseeName: string,
    assesseeRank: string,
    assessorId: number | null,
    assessors: TaskAssessorOption[],
    assessorName?: string | null,
    assessorRank?: string | null,
) => {
    const assessor = getAssessorText(assessorId, assessors, assessorName, assessorRank);

    if (showStatusText) {
        return assessor;
    }

    const assessee = [assesseeName, assesseeRank].filter(Boolean).join(', ');

    return assessor ? `${assessee} - Assessor: ${assessor}` : assessee;
};

export const openEvaluationLogPage = (pathwayId: string | number, taskId: number, isAssessorView = false) => {
    openInNewTab(getTaskRoute(pathwayId, taskId, ROUTES.SIDEBAR.LEARNING_TASK.EVALUATION_LOG, isAssessorView));
};

export const openPrintEvaluationLogPage = (pathwayId: string | number, taskId: number, isAssessorView = false) => {
    openInNewTab(getTaskRoute(pathwayId, taskId, ROUTES.SIDEBAR.LEARNING_TASK.PRINT_EVALUATION_LOG, isAssessorView));
};
